import React, { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { businessInfo } from '../../mockData';
import { API_URL, track } from '../../lib/shopApi';
import DoorPhotos from './DoorPhotos';

// "Send us your door": the shop's own enquiry, with photos attached. Posts to the same
// /api/contact endpoint as the site form (server.py) so it lands in the same inbox, with
// the photos as email attachments and a shop pageSource so Hugh can tell it apart.
// `about` / `product` prefill what the customer is asking about (chooser answers, a product).
export default function DoorEnquiryForm({ about = '', product = null, pageSource = 'shop-door-photos', testId = 'door-enquiry' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', suburb: '', about, message: '', honeypot: '' });
  const [photos, setPhotos] = useState([]);
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!API_URL) {
      setState('error');
      setError(`Online enquiries are briefly unavailable — email your photos to ${businessInfo.email} or call ${businessInfo.phone}.`);
      return;
    }
    setState('sending');
    const lines = [];
    if (product) lines.push(`Product: ${product.name} (${window.location.origin}/shop/${product.primaryCategory}/${product.slug})`);
    if (form.about) lines.push(`About: ${form.about}`);
    if (form.suburb) lines.push(`Suburb: ${form.suburb}`);
    lines.push(`Photos: ${photos.length} attached`);
    const message = `${lines.join('\n')}\n\n${form.message}`.trim();
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: '',
          projectType: 'Shop — door photos for a fit check',
          pageSource,
          message: message.length < 10 ? `${message}\n(no message)` : message,
          honeypot: form.honeypot,
          photos: photos.map((p) => ({ filename: p.name, contentType: p.type, data: p.data })),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not send');
      setState('sent');
      track('door_enquiry_sent', { photos: photos.length, product: product ? product.slug : null, about: form.about || null });
    } catch (err) {
      setState('error');
      setError(typeof err.message === 'string' ? err.message : 'Could not send — please call us.');
    }
  };

  if (state === 'sent') {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-800" role="status" data-testid={`${testId}-sent`}>
        <p className="flex items-start gap-2 font-semibold"><CheckCircle className="mt-0.5 shrink-0" size={18} /> Got it — {photos.length ? `${photos.length} photo${photos.length === 1 ? '' : 's'} and ` : ''}your details are with our team.</p>
        <p className="text-sm mt-2">We reply with what fits and the fitted price, usually the same working day.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-5" data-testid={testId}>
      <div>
        <p className="font-semibold text-gray-900 mb-2">1. Photos of the door</p>
        <DoorPhotos photos={photos} onChange={setPhotos} testId={`${testId}-photos`} />
      </div>
      <div>
        <p className="font-semibold text-gray-900 mb-2">2. What is it about?</p>
        <Input name="about" value={form.about} onChange={set} placeholder="e.g. E-LOK 905 for a narrow aluminium door, or: which lock fits my front door?" aria-label="What is it about" />
        {product && <p className="text-xs text-gray-600 mt-1">Asking about: <span className="font-semibold">{product.name}</span></p>}
      </div>
      <div>
        <p className="font-semibold text-gray-900 mb-2">3. Your details</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${testId}-name`}>Name</Label>
            <Input id={`${testId}-name`} name="name" required minLength={2} value={form.name} onChange={set} autoComplete="name" />
          </div>
          <div>
            <Label htmlFor={`${testId}-email`}>Email</Label>
            <Input id={`${testId}-email`} name="email" type="email" required value={form.email} onChange={set} autoComplete="email" />
          </div>
          <div>
            <Label htmlFor={`${testId}-phone`}>Phone</Label>
            <Input id={`${testId}-phone`} name="phone" value={form.phone} onChange={set} autoComplete="tel" />
          </div>
          <div>
            <Label htmlFor={`${testId}-suburb`}>Suburb</Label>
            <Input id={`${testId}-suburb`} name="suburb" value={form.suburb} onChange={set} placeholder="e.g. Takapuna" autoComplete="address-level2" />
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor={`${testId}-message`}>Anything else about the door? (optional)</Label>
        <Textarea id={`${testId}-message`} name="message" rows={3} value={form.message} onChange={set} placeholder="e.g. aluminium door, handle lifts to lock, the frame strike is a bit chewed" />
      </div>
      <input type="text" name="honeypot" value={form.honeypot} onChange={set} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      <Button type="submit" disabled={state === 'sending'} size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold">
        {state === 'sending' ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Send className="mr-2" size={18} />}
        {state === 'sending' ? 'Sending…' : 'Send'}
      </Button>
      <p className="text-xs text-gray-600">Or email the photos to <a href={`mailto:${businessInfo.email}`} className="underline">{businessInfo.email}</a>. We only use them to work out what fits your door.</p>
    </form>
  );
}
